import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

const TooltipContext = createContext({ show: () => {}, hide: () => {} });

/** `show(title, rows, x, y)` / `hide()` of the floating tooltip shared by the charts of the screen. */
export const useStatsTooltip = () => useContext(TooltipContext);

/**
 * Owns the single floating tooltip of the statistics screen (trend crosshair, channel stack,
 * heatmap cells). It is a fixed-position `role="status"` box, so it is announced when the crosshair
 * moves with the keyboard, and it never intercepts the pointer.
 *
 * `rows` are `{value, label?, color?, rect?}`; `color` draws a key (a line, or a square when
 * `rect`). Escape and any scroll dismiss it, so it never lingers away from what it describes.
 * Must render inside the `.zoco-stats` root so the scoped styles apply.
 */
export function StatsTooltipProvider({ children }) {
  const [tip, setTip] = useState(null);
  const [position, setPosition] = useState({ left: 0, top: 0 });
  const boxRef = useRef(null);

  const show = useCallback((title, rows, x, y) => setTip({ title, rows, x, y }), []);
  const hide = useCallback(() => setTip(null), []);
  const value = useMemo(() => ({ show, hide }), [show, hide]);

  // Offset from the pointer, flipping to the other side when it would leave the viewport.
  useLayoutEffect(() => {
    if (!tip || !boxRef.current) return;
    const { offsetWidth: width, offsetHeight: height } = boxRef.current;
    const x = Number(tip.x) || 0; // some synthetic pointer events carry no coordinates
    const y = Number(tip.y) || 0;
    let left = x + 14;
    let top = y + 14;
    if (left + width > window.innerWidth - 8) left = x - width - 14;
    if (top + height > window.innerHeight - 8) top = y - height - 14;
    setPosition({ left: Math.max(8, left), top: Math.max(8, top) });
  }, [tip]);

  useEffect(() => {
    if (!tip) return undefined;
    const onKeyDown = (event) => event.key === "Escape" && setTip(null);
    const dismiss = () => setTip(null);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", dismiss, { passive: true, capture: true });
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", dismiss, { capture: true });
    };
  }, [tip]);

  return (
    <TooltipContext.Provider value={value}>
      {children}
      {tip && (
        <div ref={boxRef} className="stats-tooltip" role="status" style={{ left: position.left, top: position.top }}>
          {tip.title && <div className="stats-tooltip__title">{tip.title}</div>}
          {tip.rows.map((row, index) => (
            <div key={index} className="stats-tooltip__row">
              {row.color && <i className={row.rect ? "stats-key-rect" : "stats-key-line"} style={{ background: row.color }} />}
              <strong>{row.value}</strong>
              {row.label && <span>{row.label}</span>}
            </div>
          ))}
        </div>
      )}
    </TooltipContext.Provider>
  );
}
